import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { errorText } from '../../../../shared/errorMessage'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import { askConfirm } from '../../../../shared/confirmDialog'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import { PlainTextarea } from '../../../../UI/PlainField'

import styles from './detail.module.scss'

/**
 * Eine eigene Anfrage: was daraus geworden ist, und was ich tun kann.
 *
 * Drei Dinge stehen hier und sonst nirgends:
 *  - der **Verlauf**, aber nur die sichtbaren Beiträge und die eigenen,
 *  - ein Feld zum Nachschreiben oder Antworten,
 *  - die drei Handlungen: bestätigen, nachhaken, zurückziehen.
 *
 * Welche davon gerade gelten, sagt der Server (`canConfirm`, `canWithdraw`,
 * `awaitsMyAnswer`) — die Oberfläche rechnet das nicht nach. Zwei Stellen, die
 * denselben Zustand herleiten, laufen beim nächsten Status auseinander.
 */
export default function RequestDetail({ id, onClose, onChanged }) {
	const { t } = useTranslation()
	const [anfrage, setAnfrage] = useState(null)
	const [text, setText] = useState('')
	const [sendet, setSendet] = useState(false)

	// Siehe unten: die Übersetzungsfunktion über eine Referenz, damit sie in
	// keiner Abhängigkeitsliste steht.
	const uebersetze = useRef(t)
	uebersetze.current = t

	/*
	 * `t` gehört **nicht** in die Abhängigkeiten: `useTranslation()` gibt bei
	 * jedem Render eine neue Referenz, und mit ihr in der Liste liefe das Laden
	 * in die Schleife Render → Laden → Render (siehe shared/translationDeps.test).
	 * Der Text wird nur im Fehlerfall gebraucht und ist dann aktuell genug.
	 */
	const laden = useCallback(async () => {
		try {
			const { data } = await http.get(`${TApi.REQUESTS}/${id}`, { disableToast: true })
			setAnfrage(data)
		} catch (error) {
			toast.error(errorText(error, uebersetze.current('This request could not be loaded.')))
			onClose()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, onClose])

	useEffect(() => {
		laden()
	}, [laden])

	const backdropProps = useDialogDismiss(onClose, { enabled: !sendet })

	const schreibe = async () => {
		if (!text.trim() || sendet) return
		setSendet(true)
		try {
			const { data } = await http.post(
				`${TApi.REQUESTS}/${id}/comments`,
				{ body: text.trim() },
				{ disableToast: true }
			)
			setAnfrage(data)
			setText('')
			onChanged()
		} catch (error) {
			toast.error(errorText(error, t('Your message could not be sent.')))
		} finally {
			setSendet(false)
		}
	}

	const handle = async aktion => {
		if (sendet) return

		/*
		 * Zurückziehen ist die einzige Handlung, die etwas wegnimmt — und die
		 * einzige, die deshalb nachfragt. Bestätigen und Nachhaken lassen sich
		 * beide wieder einfangen.
		 */
		if (aktion === 'withdraw') {
			const sicher = await askConfirm({
				title: t('Withdraw request?'),
				text: t('The request will be closed. You can always submit a new one.'),
				confirmLabel: t('Withdraw'),
				tone: 'danger'
			})
			if (!sicher) return
		}

		setSendet(true)
		try {
			const { data } = await http.post(
				`${TApi.REQUESTS}/${id}/actions/${aktion}`,
				{},
				{ disableToast: true }
			)
			setAnfrage(data)
			onChanged()
			if (aktion === 'confirm') toast.success(t('Thank you — the request is closed.'))
		} catch (error) {
			toast.error(errorText(error, t('That did not work.')))
			laden()
		} finally {
			setSendet(false)
		}
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			<div className={styles.dialog} role="dialog" aria-modal="true">
				<div className={styles.header}>
					<h3 className={styles.title}>
						{anfrage ? `#${anfrage.id} ${anfrage.title}` : t('Request')}
					</h3>
					<button type="button" className={styles.close} aria-label={t('Close')} onClick={onClose}>
						<svg viewBox="0 0 12 12" aria-hidden="true">
							<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" />
						</svg>
					</button>
				</div>

				{!anfrage ? (
					<Loading />
				) : (
					<div className={styles.body}>
						<div className={styles.statusRow}>
							<span
								className={clsx(
									styles.status,
									anfrage.awaitsMyAnswer && styles.statusAction,
									anfrage.canConfirm && styles.statusReview,
									anfrage.closed && styles.statusClosed
								)}
							>
								{anfrage.status}
							</span>
							{anfrage.rejectionReason && (
								<span className={styles.reason}>{anfrage.rejectionReason}</span>
							)}
						</div>

						<p className={styles.description}>{anfrage.description}</p>

						{anfrage.attachments.length > 0 && (
							<ul className={styles.files}>
								{anfrage.attachments.map(datei => (
									<li key={datei.id}>{datei.name}</li>
								))}
							</ul>
						)}

						<div className={styles.timeline}>
							{anfrage.entries.map(eintrag => (
								<div
									key={eintrag.id}
									className={clsx(styles.entry, eintrag.mine && styles.entryMine)}
								>
									<span className={styles.entryAuthor}>{eintrag.author}</span>
									<p className={styles.entryBody}>{eintrag.body}</p>
									{eintrag.attachments.length > 0 && (
										<ul className={styles.entryFiles}>
											{eintrag.attachments.map(datei => (
												<li key={datei.id}>{datei.name}</li>
											))}
										</ul>
									)}
								</div>
							))}
						</div>

						{!anfrage.closed && (
							<>
								<PlainTextarea
									label={anfrage.awaitsMyAnswer ? t('Your answer') : t('Add something')}
									rows={3}
									value={text}
									onChange={event => setText(event.target.value)}
								/>

								<div className={styles.actions}>
									<Btn type="button" onClick={schreibe} disabled={sendet || !text.trim()}>
										{t('Send')}
									</Btn>

									{anfrage.canConfirm && (
										<>
											<Btn
												theme="success"
												type="button"
												onClick={() => handle('confirm')}
												disabled={sendet}
											>
												{t('That solves it')}
											</Btn>
											<Btn
												theme="secondary"
												type="button"
												onClick={() => handle('reopen')}
												disabled={sendet}
											>
												{t('Not yet solved')}
											</Btn>
										</>
									)}

									{anfrage.canWithdraw && (
										<Btn
											theme="minimal"
											type="button"
											onClick={() => handle('withdraw')}
											disabled={sendet}
										>
											{t('Withdraw')}
										</Btn>
									)}
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

RequestDetail.propTypes = {
	id: PropTypes.number.isRequired,
	onClose: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired
}
